import { useState, useEffect, useRef, useCallback, useMemo } from 'react';

/**
 * Workspace layout preferences version for migration
 */
const CURRENT_PREFS_VERSION = 1;

/**
 * Default preferences for first-time users
 */
const DEFAULT_PREFS = {
  v: CURRENT_PREFS_VERSION,
  coursesCollapsed: false,
  deskCollapsed: true,
  chatExpanded: false,
  updatedAt: Date.now(),
};

/**
 * Migrate preferences from older versions
 * @param {any} raw - Raw preference data from localStorage
 * @returns {Object} Migrated preferences
 */
function migratePrefs(raw) {
  if (!raw || typeof raw !== 'object') {
    return { ...DEFAULT_PREFS };
  }
  
  const version = raw.v || 0;
  let prefs = { ...raw };
  
  // v0 → v1: Add version and ensure all fields exist
  if (version < 1) {
    prefs = {
      v: 1,
      coursesCollapsed: raw.coursesCollapsed ?? false,
      deskCollapsed: raw.deskCollapsed ?? true,
      chatExpanded: raw.chatExpanded ?? false,
      updatedAt: raw.updatedAt || Date.now(),
    };
  }
  
  // Future migrations go here:
  // if (version < 2) { ... }
  
  return prefs;
}

/**
 * Get layout preferences for a specific user
 * @param {string|null} userId - User ID or null for anonymous
 * @returns {Object} Preferences object
 */
function getLayoutPrefs(userId) {
  const storageKey = `uiPrefs:${userId || 'anon'}:workspaceLayout`;
  
  try {
    const stored = localStorage.getItem(storageKey);
    if (!stored) {
      return { ...DEFAULT_PREFS };
    }
    
    const parsed = JSON.parse(stored);
    return migratePrefs(parsed);
  } catch (err) {
    console.warn('Failed to load layout preferences:', err);
    return { ...DEFAULT_PREFS };
  }
}

/**
 * Save layout preferences for a specific user
 * @param {string|null} userId - User ID or null for anonymous
 * @param {Object} prefs - Preferences to save
 */
function setLayoutPrefs(userId, prefs) {
  const storageKey = `uiPrefs:${userId || 'anon'}:workspaceLayout`;
  
  try {
    const toSave = {
      ...prefs,
      v: CURRENT_PREFS_VERSION,
      updatedAt: Date.now(),
    };
    localStorage.setItem(storageKey, JSON.stringify(toSave));
  } catch (err) {
    console.warn('Failed to save layout preferences:', err);
  }
}

/**
 * Custom hook for debounced viewport width
 * @param {number} delay - Debounce delay in ms
 * @returns {number} Debounced viewport width
 */
function useDebouncedViewportWidth(delay = 120) {
  const [width, setWidth] = useState(() => 
    typeof window !== 'undefined' ? window.innerWidth : 1920
  );
  
  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    let timeoutId;
    
    const handleResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        setWidth(window.innerWidth);
      }, delay);
    };
    
    window.addEventListener('resize', handleResize);
    
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', handleResize);
    };
  }, [delay]);
  
  return width;
}

/**
 * Main workspace layout hook
 * Manages collapse state, responsive breakpoints, transitions, and persistence
 * 
 * @param {string|null} userId - Current user ID from AuthContext
 * @returns {Object} Layout state and handlers
 */
export function useWorkspaceLayout(userId = null) {
  const viewportWidth = useDebouncedViewportWidth(120);
  
  // Load initial preferences
  const [coursesCollapsed, setCoursesCollapsed] = useState(() => {
    const prefs = getLayoutPrefs(userId);
    // Apply smart defaults based on viewport
    if (viewportWidth >= 1180) return prefs.coursesCollapsed;
    if (viewportWidth >= 820) return true; // Mid-size: courses collapsed by default
    return prefs.coursesCollapsed;
  });
  
  const [deskCollapsed, setDeskCollapsed] = useState(() => {
    const prefs = getLayoutPrefs(userId);
    return prefs.deskCollapsed;
  });
  
  // Ephemeral drawer state (not persisted)
  const [activeDrawer, setActiveDrawer] = useState(null);
  
  // Transition state machine
  const [transitionState, setTransitionState] = useState('idle');
  const transitionTokenRef = useRef(0);
  const lastToggleAtRef = useRef({ courses: 0, desk: 0 });
  const lastActiveElementRef = useRef(null);
  const focusTargetRefs = useRef({ courses: null, desk: null });
  
  // Detect reduced motion preference
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  });
  
  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const handler = (e) => setPrefersReducedMotion(e.matches);
    
    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, []);
  
  // Compute storage key for this user
  const storageKey = useMemo(() => 
    `uiPrefs:${userId || 'anon'}:workspaceLayout`,
    [userId]
  );
  
  // Save preferences when they change
  useEffect(() => {
    setLayoutPrefs(userId, {
      coursesCollapsed,
      deskCollapsed,
      chatExpanded: false, // Placeholder for chat state
    });
  }, [userId, coursesCollapsed, deskCollapsed]);
  
  // Cross-tab sync via storage event
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key !== storageKey) return;
      if (!e.newValue) return;
      
      try {
        const parsed = JSON.parse(e.newValue);
        const migrated = migratePrefs(parsed);
        
        // Update state from other tab
        setCoursesCollapsed(migrated.coursesCollapsed);
        setDeskCollapsed(migrated.deskCollapsed);
      } catch (err) {
        console.warn('Failed to sync layout from other tab:', err);
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [storageKey]);
  
  // Sync on user change (logout/login)
  useEffect(() => {
    const prefs = getLayoutPrefs(userId);
    setCoursesCollapsed(prefs.coursesCollapsed);
    setDeskCollapsed(prefs.deskCollapsed);
    // Close any open drawers on auth change
    setActiveDrawer(null);
  }, [userId]);
  
  // Compute effective layout based on viewport
  const useDrawerMode = viewportWidth < 820;
  const isResizingCourses = transitionState.includes('courses') && transitionState !== 'idle';
  const isResizingDesk = transitionState.includes('desk') && transitionState !== 'idle';
  
  const effectiveCoursesCollapsed = useMemo(() => {
    if (useDrawerMode) return coursesCollapsed;
    if (viewportWidth >= 820) return coursesCollapsed;
    return coursesCollapsed;
  }, [coursesCollapsed, useDrawerMode, viewportWidth]);
  
  const effectiveDeskCollapsed = useMemo(() => {
    if (useDrawerMode) return deskCollapsed;
    return deskCollapsed;
  }, [deskCollapsed, useDrawerMode]);
  
  // Transition orchestration with fail-safe watchdog
  const startTransition = useCallback((newState, panel) => {
    const token = ++transitionTokenRef.current;
    setTransitionState(newState);
    
    // Watchdog timeout (700ms) to prevent stuck animations
    const watchdogTimeout = setTimeout(() => {
      if (transitionTokenRef.current === token) {
        console.warn(`[Layout] Watchdog triggered for ${newState}, forcing idle`);
        setTransitionState('idle');
      }
    }, 700);
    
    return { token, watchdogTimeout };
  }, []);
  
  const endTransition = useCallback((token, watchdogTimeout) => {
    if (transitionTokenRef.current === token) {
      setTransitionState('idle');
    }
    clearTimeout(watchdogTimeout);
  }, []);
  
  // Toggle functions with click throttle and sequential transitions
  const toggleCourses = useCallback(() => {
    const now = Date.now();
    const lastToggle = lastToggleAtRef.current.courses;
    
    // Click throttle: ignore rapid clicks within 100ms
    if (now - lastToggle < 100) {
      return;
    }
    
    lastToggleAtRef.current.courses = now;
    
    const isCurrentlyCollapsed = coursesCollapsed;
    const newState = !isCurrentlyCollapsed;
    
    // If reduced motion, just toggle immediately
    if (prefersReducedMotion) {
      setCoursesCollapsed(newState);
      return;
    }
    
    // Sequential transition
    if (newState) {
      // Collapsing: fade out content → resize grid
      const { token, watchdogTimeout } = startTransition('collapsing-courses', 'courses');
      
      // Fade content (100ms)
      setTimeout(() => {
        if (transitionTokenRef.current !== token) return;
        
        // Then resize (160ms)
        setCoursesCollapsed(true);
        
        setTimeout(() => {
          endTransition(token, watchdogTimeout);
        }, 160);
      }, 100);
    } else {
      // Expanding: resize grid → fade in content
      const { token, watchdogTimeout } = startTransition('expanding-courses', 'courses');
      
      setCoursesCollapsed(false);
      
      // Resize (160ms) then fade in (100ms)
      setTimeout(() => {
        if (transitionTokenRef.current !== token) return;
        
        setTimeout(() => {
          endTransition(token, watchdogTimeout);
        }, 100);
      }, 160);
    }
  }, [coursesCollapsed, prefersReducedMotion, startTransition, endTransition]);
  
  const toggleDesk = useCallback(() => {
    const now = Date.now();
    const lastToggle = lastToggleAtRef.current.desk;
    
    // Click throttle: ignore rapid clicks within 100ms
    if (now - lastToggle < 100) {
      return;
    }
    
    lastToggleAtRef.current.desk = now;
    
    const isCurrentlyCollapsed = deskCollapsed;
    const newState = !isCurrentlyCollapsed;
    
    // If reduced motion, just toggle immediately
    if (prefersReducedMotion) {
      setDeskCollapsed(newState);
      return;
    }
    
    // Sequential transition
    if (newState) {
      // Collapsing
      const { token, watchdogTimeout } = startTransition('collapsing-desk', 'desk');
      
      setTimeout(() => {
        if (transitionTokenRef.current !== token) return;
        
        setDeskCollapsed(true);
        
        setTimeout(() => {
          endTransition(token, watchdogTimeout);
        }, 160);
      }, 100);
    } else {
      // Expanding
      const { token, watchdogTimeout } = startTransition('expanding-desk', 'desk');
      
      setDeskCollapsed(false);
      
      setTimeout(() => {
        if (transitionTokenRef.current !== token) return;
        
        setTimeout(() => {
          endTransition(token, watchdogTimeout);
        }, 100);
      }, 160);
    }
  }, [deskCollapsed, prefersReducedMotion, startTransition, endTransition]);
  
  // Drawer functions (mobile only)
  const openDrawer = useCallback((side) => {
    if (!useDrawerMode) return;
    
    // Store current focus
    lastActiveElementRef.current = document.activeElement;
    
    setActiveDrawer(side);
    
    // Focus management after drawer opens
    requestAnimationFrame(() => {
      const targetRef = focusTargetRefs.current[side];
      if (targetRef?.current) {
        targetRef.current.focus();
      }
    });
  }, [useDrawerMode]);
  
  const closeDrawer = useCallback(() => {
    setActiveDrawer(null);
    
    // Restore focus
    requestAnimationFrame(() => {
      if (lastActiveElementRef.current) {
        lastActiveElementRef.current.focus?.();
      }
    });
  }, []);
  
  // Cleanup on unmount
  useEffect(() => {
    const tokenRef = transitionTokenRef;
    return () => {
      // Clear any pending transition timers by incrementing the token
      tokenRef.current++;
    };
  }, []);
  
  return {
    // State
    effectiveCoursesCollapsed,
    effectiveDeskCollapsed,
    useDrawerMode,
    activeDrawer,
    transitionState,
    isResizingCourses,
    isResizingDesk,
    prefersReducedMotion,
    viewportWidth,
    
    // Actions
    toggleCourses,
    toggleDesk,
    openDrawer,
    closeDrawer,
    
    // Refs
    focusTargetRefs,
  };
}
